/**
 * Session Persistence Utilities
 * Manages localStorage for active charging sessions
 * Enables rehydration after page refresh, browser close, or app backgrounding
 * 
 * UPDATED FOR BACKEND-AUTHORITATIVE SESSION HANDLING:
 * - Stores minimal session context (sessionUid, stationId, connectorId)
 * - Backend is source of truth for all session data
 * - Cleared when session becomes terminal (completed/stopped/failed/voided)
 */

const ACTIVE_SESSION_KEY = 'ev_active_session_uid';
const LEGACY_KEY = 'ev:lastActiveSession'; // Old key for migration

/**
 * Save active session context to localStorage
 * Called when session becomes active or monitoring screen loads successfully
 * 
 * @param {object} sessionData - Active session context
 * @param {string} sessionData.sessionUid - Session UID
 * @param {string} sessionData.stationId - Station ID
 * @param {number} sessionData.connectorId - Connector ID
 * @param {string} sessionData.startedAt - Session start timestamp (ISO string)
 * @param {string} sessionData.createdAt - Session creation timestamp (ISO string)
 */
export function saveActiveSession(sessionData) {
  try {
    const { sessionUid, stationId, publicId, connectorId, startedAt, createdAt } = sessionData;

    // Validate required fields
    if (!sessionUid || !stationId || typeof connectorId !== 'number') {
      console.warn('[SessionPersistence] Cannot save - missing required fields:', {
        hasSessionUid: !!sessionUid,
        hasStationId: !!stationId,
        hasConnectorId: typeof connectorId === 'number'
      });
      return false;
    }

    const sessionContext = {
      sessionUid,
      stationId,
      publicId: publicId || null,
      connectorId,
      startedAt: startedAt || new Date().toISOString(),
      createdAt: createdAt || new Date().toISOString(),
      savedAt: new Date().toISOString()
    };
    
    localStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify(sessionContext));
    
    console.log('[SessionPersistence] ✓ Saved active session:', {
      sessionUid,
      stationId,
      connectorId
    });
    
    return true;
  } catch (error) {
    console.error('[SessionPersistence] Failed to save active session:', error);
    return false;
  }
}

/**
 * Get active session context from localStorage
 * Returns null if no session is saved or data is invalid
 * 
 * @returns {object|null} Session context or null
 */
export function getActiveSession() {
  try {
    const raw = localStorage.getItem(ACTIVE_SESSION_KEY);
    
    if (!raw) {
      return null;
    }
    
    const sessionContext = JSON.parse(raw);
    
    // Validate structure
    if (!sessionContext.sessionUid || !sessionContext.stationId || typeof sessionContext.connectorId !== 'number') {
      console.warn('[SessionPersistence] Invalid session context in localStorage - clearing');
      clearActiveSession();
      return null;
    }
    
    // Check if session is too old (more than 24 hours)
    const savedAt = new Date(sessionContext.savedAt || sessionContext.createdAt);
    const ageHours = (Date.now() - savedAt.getTime()) / 1000 / 60 / 60;
    
    if (ageHours > 24) {
      console.warn('[SessionPersistence] Session context is stale (age:', ageHours.toFixed(1), 'hours) - clearing');
      clearActiveSession();
      return null;
    }
    
    console.log('[SessionPersistence] ✓ Retrieved active session:', {
      sessionUid: sessionContext.sessionUid,
      stationId: sessionContext.stationId,
      connectorId: sessionContext.connectorId,
      ageMinutes: (ageHours * 60).toFixed(1)
    });
    
    return sessionContext;
  } catch (error) {
    console.error('[SessionPersistence] Failed to retrieve active session:', error);
    clearActiveSession();
    return null;
  }
}

/**
 * Clear active session from localStorage
 * Called when session ends or becomes invalid
 */
export function clearActiveSession() {
  try {
    localStorage.removeItem(ACTIVE_SESSION_KEY);
    console.log('[SessionPersistence] ✓ Cleared active session');
    return true;
  } catch (error) {
    console.error('[SessionPersistence] Failed to clear active session:', error);
    return false;
  }
}

/**
 * Check if an active session exists in localStorage
 * @returns {boolean}
 */
export function hasActiveSession() {
  return !!getActiveSession();
}
